import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router';
import { ErrorNote, Loading } from '../../components/ui';
import { api, type SettingsStatus } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { AdminShell, Panel, StatusDot, Tile } from './shared';

const LLM_LABEL: Record<string, string> = { anthropic: 'Claude (Anthropic)', gemini: 'Gemini (Google)', mock: 'Mock (demo)' };
const DAYS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** What runs the platform: providers, models, calling hours. Customers never see this. */
export function AdminSystemPage() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api.get<SettingsStatus>('/api/settings/status'), enabled: !!user?.platformAdmin, refetchInterval: 30_000 });
  if (!user?.platformAdmin) return <Navigate to="/" replace />;
  const s = q.data;
  const p = s?.providers;
  const r = s?.ready;
  const down = r ? Object.values(r).filter((x) => !x).length : 0;
  return (
    <AdminShell title="System" subtitle="The services behind every interview, and when calls may go out. This page is the platform's own; customers see none of it.">
      <ErrorNote error={q.error} />
      {q.isLoading && <Loading />}
      {s && r && p && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Tile label="Services" value={down === 0 ? 'All live' : `${down} off`} tone={down === 0 ? 'good' : 'warn'} note="AI, voice, phone, email" />
            <Tile label="Interviews in progress" value={s.liveSessions} note="your own company's" />
            <Tile label="At once" value={p.telephony.maxConcurrent} note="interviews the box takes at a time" />
            <Tile label="Calling hours" value={s.calling.window.split(' ')[0] ?? s.calling.window} note={s.calling.days.split(',').map((d) => DAYS[Number(d)]).join(', ')} />
          </div>
          <div className="grid gap-5 xl:grid-cols-2">
            <Panel title="What works" description="Each service, as configured on this server.">
              <ul className="divide-y divide-white/10">
                {[
                  ['AI interviewer and scorer', r.ai, r.ai ? (LLM_LABEL[p.llm.provider] ?? p.llm.provider) : 'Demo mode'],
                  ['Voice', r.voice, r.voice ? 'Sarvam' : 'Demo mode'],
                  ['Phone calls', r.phone, r.phone ? p.telephony.provider : 'Not set up'],
                  ['Email', r.email, r.email ? 'SMTP' : 'Not set up'],
                ].map(([label, ok, value]) => (
                  <li key={String(label)} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 text-sm">
                    <span>{label}</span>
                    <StatusDot on={Boolean(ok)} label={String(value)} />
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel title="Models and voices" description="Set in the server's environment; a deploy changes them.">
              <dl className="divide-y divide-white/10 text-sm">
                {[
                  ['Interviewer brain', p.llm.interviewModel || 'provider default'],
                  ['Scorer', p.llm.scoringModel || 'provider default'],
                  ['Speech to text', p.voice.stt],
                  ['Text to speech', `${p.voice.tts} · ${p.voice.speaker}`],
                  ...(p.voice.voices ? [['Voices per language', p.voice.voices]] : []),
                  ['Phone provider', p.telephony.provider === 'none' ? 'Not configured' : p.telephony.provider],
                  ['Public address', p.publicBaseUrl],
                ].map(([k, v]) => (
                  <div key={String(k)} className="flex items-center justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
                    <dt className="text-white/55">{k}</dt>
                    <dd className="text-right font-mono text-xs">{v}</dd>
                  </div>
                ))}
              </dl>
            </Panel>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
